// Recharts components for ZE tender analytics.
// Design rules (per dataviz skill): SINGLE axis only (no dual-axis), semantic status colors
// for outcomes with a legend + tooltip as secondary encoding, thin recessive grid/axes,
// graceful sparse states. Stacked outcome-count bars encode BOTH volume (bar length) and
// win-rate (green fraction) on one axis — no second scale needed.
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
  useXAxisScale,
  useYAxisScale,
} from "recharts";

import type { BarLabelConfig, CountRow, WinRateGroup } from "./types";

const C = {
  won: "#0ca30c", // status: good
  lost: "#d03b3b", // status: critical
  cancelled: "#fab219", // status: warning
  series: "#2a78d6", // categorical slot 1 (blue) — the win-rate / EV metric
  best: "#0ca30c",
  grid: "rgba(130,130,130,0.18)",
  axis: "#898781",
};

export const pct = (v: number | null | undefined) =>
  v == null ? "—" : `${Math.round(v * 100)}%`;

// Multiplier is already stored as a percentage (100 = 100%), so it is NOT scaled by pct.
const asMult = (v: number | null | undefined) => (v == null ? "—" : `${Math.round(v)}%`);

// Compact PLN for the on-bar label ("1,2 mln" / "340 tys.") — full money would overflow.
function compactMoney(v: number): string {
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1).replace(".", ",")} mln`;
  if (v >= 1_000) return `${Math.round(v / 1_000)} tys.`;
  return `${Math.round(v)}`;
}

/** The stat text to center on a bar for the chosen metric+statistic, or null if no data. */
function barLabelText(g: WinRateGroup, cfg?: BarLabelConfig | null): string | null {
  if (!cfg) return null;
  const s = g.stats?.[cfg.metric];
  const v = s?.[cfg.stat];
  if (v == null) return null;
  return cfg.metric === "value" ? compactMoney(v) : `${Math.round(v)}%`;
}

/**
 * SVG labels centered on each horizontal stacked bar. Rendered as a child of the chart so it
 * can read the axis scales (recharts 3 `useXAxisScale`/`useYAxisScale`): x at the bar's
 * midpoint (value axis, n/2), y at the category band center. Outlined for legibility over the
 * colored segments.
 */
function BarCenterLabels({ rows, cfg }: { rows: WinRateGroup[]; cfg?: BarLabelConfig | null }) {
  const xScale = useXAxisScale();
  const yScale = useYAxisScale();
  if (!cfg || !xScale || !yScale) return null;
  return (
    <g>
      {rows.map((g) => {
        const text = barLabelText(g, cfg);
        if (!text) return null;
        const total = g.won + g.lost + g.cancelled;
        const cx = xScale(total / 2);
        const cy = yScale(g.category, { position: "middle" });
        if (cx == null || cy == null) return null;
        return (
          <text
            key={g.category}
            x={cx}
            y={cy}
            textAnchor="middle"
            dominantBaseline="central"
            style={{
              fontSize: 11,
              fontWeight: 700,
              fill: "#fff",
              paintOrder: "stroke",
              stroke: "rgba(0,0,0,0.6)",
              strokeWidth: 3,
              strokeLinejoin: "round",
              pointerEvents: "none",
            }}
          >
            {text}
          </text>
        );
      })}
    </g>
  );
}

function Empty({ note }: { note?: string }) {
  return (
    <div className="py-10 text-center text-sm text-gray-400">
      {note ?? "Brak danych"}
    </div>
  );
}

function OutcomeLegend() {
  const items: [string, string][] = [
    ["Wygrane", C.won],
    ["Przegrane", C.lost],
    ["Anulowane", C.cancelled],
  ];
  return (
    <div className="mb-1 flex flex-wrap gap-3 text-xs text-gray-500 dark:text-gray-400">
      {items.map(([label, color]) => (
        <span key={label} className="flex items-center gap-1.5">
          <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: color }} />
          {label}
        </span>
      ))}
    </div>
  );
}

interface GroupTooltipInput {
  active?: boolean;
  payload?: Array<{ payload: WinRateGroup }>;
}

function GroupTooltip({ active, payload }: GroupTooltipInput) {
  if (!active || !payload || payload.length === 0) return null;
  const g = payload[0].payload;
  return (
    <div className="rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-xs shadow-sm dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100">
      <div className="font-semibold">{g.category}</div>
      <div>
        win-rate <b>{pct(g.win_rate)}</b> · n={g.n}
        {g.low_sample ? " · mała próba" : ""}
      </div>
      <div className="text-gray-500 dark:text-gray-400">
        Wygrane {g.won} · Przegrane {g.lost} · Anulowane {g.cancelled}
      </div>
      {g.stats?.multiplier &&
        (g.stats.multiplier.median != null || g.stats.multiplier.avg != null) && (
          <div className="text-gray-500 dark:text-gray-400">
            mnożnik: mediana <b>{asMult(g.stats.multiplier.median)}</b> · śr.{" "}
            {asMult(g.stats.multiplier.avg)}
          </div>
        )}
    </div>
  );
}

/**
 * Horizontal stacked outcome-count bars. Bar length = volume (n); the green fraction of
 * each bar visually IS the win-rate — so one axis carries both. `ordered` keeps the input
 * order (for buckets); otherwise sorts by volume.
 */
export function OutcomeBars({
  data,
  ordered = false,
  labelConfig,
  onSelect,
}: {
  data: WinRateGroup[];
  ordered?: boolean;
  labelConfig?: BarLabelConfig | null;
  /** Clicking any segment of a bar reports that bar's category (opens the drill-down). */
  onSelect?: (category: string) => void;
}) {
  if (!data.length) return <Empty />;
  const rows = ordered ? data : [...data].sort((a, b) => b.n - a.n);
  const height = Math.max(130, rows.length * 34 + 36);
  // Recharts passes (data, index) — read the category by index into `rows` (the payload
  // shape varies across versions; the index is stable). Fall back to the datum's category.
  const handleClick = onSelect
    ? (entry: unknown, index?: number) => {
        const byIndex = index != null ? rows[index]?.category : undefined;
        const e = entry as { category?: string; payload?: { category?: string } };
        const cat = byIndex ?? e?.category ?? e?.payload?.category;
        if (cat != null) onSelect(cat);
      }
    : undefined;
  const barProps = handleClick
    ? { onClick: handleClick, cursor: "pointer" as const }
    : {};
  return (
    <div className={onSelect ? "[&_.recharts-bar-rectangle]:cursor-pointer" : undefined}>
      <OutcomeLegend />
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={rows} layout="vertical" margin={{ left: 8, right: 16, top: 4, bottom: 4 }} barCategoryGap="22%">
          <CartesianGrid horizontal={false} stroke={C.grid} />
          <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: C.axis }} axisLine={false} tickLine={false} />
          <YAxis type="category" dataKey="category" width={148} tick={{ fontSize: 11, fill: C.axis }} axisLine={false} tickLine={false} />
          <Tooltip content={<GroupTooltip />} cursor={{ fill: "rgba(130,130,130,0.08)" }} />
          <Bar dataKey="won" stackId="s" fill={C.won} maxBarSize={26} radius={[3, 0, 0, 3]} {...barProps} />
          <Bar dataKey="lost" stackId="s" fill={C.lost} maxBarSize={26} {...barProps} />
          <Bar dataKey="cancelled" stackId="s" fill={C.cancelled} maxBarSize={26} radius={[0, 3, 3, 0]} {...barProps} />
          <BarCenterLabels rows={rows} cfg={labelConfig} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

/** Single-axis win-rate line over time; friendly note when there is only one period. */
export function WinRateLine({ data }: { data: WinRateGroup[] }) {
  if (!data.length) return <Empty />;
  if (data.length === 1) {
    const g = data[0];
    return (
      <Empty note={`Win-rate ${pct(g.win_rate)} w ${g.category} (n=${g.n}). Trend pojawi się przy danych z ≥2 okresów.`} />
    );
  }
  return (
    <ResponsiveContainer width="100%" height={240}>
      <LineChart data={data} margin={{ top: 8, right: 16, left: 4, bottom: 4 }}>
        <CartesianGrid vertical={false} stroke={C.grid} />
        <XAxis dataKey="category" tick={{ fontSize: 11, fill: C.axis }} axisLine={false} tickLine={false} />
        <YAxis domain={[0, 1]} tickFormatter={pct} tick={{ fontSize: 11, fill: C.axis }} axisLine={false} tickLine={false} width={44} />
        <Tooltip content={<GroupTooltip />} />
        <Line dataKey="win_rate" stroke={C.series} strokeWidth={2} dot={{ r: 4, fill: C.series }} isAnimationActive={false} />
      </LineChart>
    </ResponsiveContainer>
  );
}

// Representative multiplier for each bucket (must match backend multiplier_bucket ranges).
const MULT_MID: Record<string, number> = {
  "<100%": 95,
  "100–110%": 105,
  "111–125%": 118,
  "126–150%": 138,
  "151–175%": 163,
  "≥176%": 190,
};

interface EVRow {
  category: string;
  ev: number;
  win_rate: number;
  margin: number;
  n: number;
  low_sample: boolean;
}

interface EVTooltipInput {
  active?: boolean;
  payload?: Array<{ payload: EVRow }>;
}

function EVTooltip({ active, payload }: EVTooltipInput) {
  if (!active || !payload || payload.length === 0) return null;
  const r = payload[0].payload;
  return (
    <div className="rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-xs shadow-sm dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100">
      <div className="font-semibold">Mnożnik {r.category}</div>
      <div>
        wartość oczek. <b>{r.ev.toFixed(1)}</b> pkt/przetarg
      </div>
      <div className="text-gray-500 dark:text-gray-400">
        win-rate {pct(r.win_rate)} × marża {r.margin} pkt · n={r.n}
        {r.low_sample ? " · mała próba" : ""}
      </div>
    </div>
  );
}

/**
 * Expected value per multiplier level = win-rate × (mnożnik − koszty wewnętrzne). Shows that
 * winning 1-in-10 at 200% can beat 50% at 100% — the peak bar is the profit-optimal multiplier.
 * Computed client-side from empirical per-bucket win-rate + the user's cost input.
 */
export function ExpectedValueChart({ groups, costPct }: { groups: WinRateGroup[]; costPct: number }) {
  const rows: EVRow[] = groups
    .filter((g) => MULT_MID[g.category] != null)
    .map((g) => {
      const mid = MULT_MID[g.category];
      const margin = mid - costPct;
      return {
        category: g.category,
        ev: Number((g.win_rate * margin).toFixed(2)),
        win_rate: g.win_rate,
        margin,
        n: g.n,
        low_sample: g.low_sample,
      };
    });
  if (!rows.length) return <Empty note="Brak wycen z mnożnikiem w tym okresie." />;
  const best = rows.reduce((a, b) => (b.ev > a.ev ? b : a), rows[0]);
  return (
    <ResponsiveContainer width="100%" height={240}>
      <BarChart data={rows} margin={{ top: 8, right: 16, left: 4, bottom: 4 }}>
        <CartesianGrid vertical={false} stroke={C.grid} />
        <XAxis dataKey="category" tick={{ fontSize: 11, fill: C.axis }} axisLine={false} tickLine={false} />
        <YAxis tick={{ fontSize: 11, fill: C.axis }} axisLine={false} tickLine={false} width={44} />
        <Tooltip content={<EVTooltip />} cursor={{ fill: "rgba(130,130,130,0.08)" }} />
        <ReferenceLine y={0} stroke={C.axis} />
        <Bar dataKey="ev" maxBarSize={54} radius={[3, 3, 0, 0]}>
          {rows.map((r, i) => (
            <Cell key={i} fill={r.category === best.category && r.ev > 0 ? C.best : C.series} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

/** Simple single-series count bars — cancelled distributions. */
// ── Price headroom (entry threshold vs win-rate) ──────────────────────────────────
// Per segment: X = typical multiplier we actually bid (median), Y = win-rate. A segment high &
// to the LEFT (win a lot, bid low) is UNDERPRICED — there is room to raise incrementally. This is
// the portfolio-level analogue of the per-tender entry-threshold guard: "długie SN: startujecie
// ~70%, wygrywacie 90% → jest zapas". Underpriced points are drawn green, the rest recessive blue.
interface HeadroomRow {
  category: string;
  bid: number;
  wr: number;
  n: number;
  underpriced: boolean;
}

const HEADROOM_WR = 0.6; // win-rate above which a low-bid segment reads as clearly underpriced
const HEADROOM_BID = 100; // ... and a typical bid below 100% means we started under full price

function HeadroomTooltip({ active, payload }: { active?: boolean; payload?: Array<{ payload: HeadroomRow }> }) {
  if (!active || !payload || payload.length === 0) return null;
  const r = payload[0].payload;
  return (
    <div className="rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-xs shadow-sm dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100">
      <div className="font-semibold">{r.category}</div>
      <div>typowy mnożnik <b>{r.bid}%</b> · win-rate <b>{pct(r.wr)}</b></div>
      <div className="text-gray-500 dark:text-gray-400">
        n={r.n}
        {r.underpriced ? " · zapas — można podbić" : ""}
      </div>
    </div>
  );
}

export function PriceHeadroomChart({ groups }: { groups: WinRateGroup[] }) {
  const rows: HeadroomRow[] = groups
    .filter((g) => g.stats?.multiplier?.median != null && g.n >= 2)
    .map((g) => {
      const bid = g.stats!.multiplier.median as number;
      return {
        category: g.category,
        bid,
        wr: g.win_rate,
        n: g.n,
        underpriced: g.win_rate >= HEADROOM_WR && bid < HEADROOM_BID,
      };
    });
  if (!rows.length) return <Empty note="Za mało segmentów z mnożnikiem." />;
  const bids = rows.map((r) => r.bid);
  const lo = Math.max(50, Math.floor((Math.min(...bids) - 5) / 5) * 5);
  const hi = Math.min(200, Math.ceil((Math.max(...bids) + 5) / 5) * 5);
  const green = rows.filter((r) => r.underpriced);
  const blue = rows.filter((r) => !r.underpriced);
  return (
    <ResponsiveContainer width="100%" height={260}>
      <ScatterChart margin={{ top: 12, right: 18, left: 4, bottom: 16 }}>
        <CartesianGrid stroke={C.grid} />
        <XAxis
          type="number"
          dataKey="bid"
          domain={[lo, hi]}
          name="typowy mnożnik"
          tickFormatter={(v) => `${v}%`}
          tick={{ fontSize: 11, fill: C.axis }}
          axisLine={false}
          tickLine={false}
          label={{ value: "typowy mnożnik startowy", position: "insideBottom", offset: -8, fontSize: 11, fill: C.axis }}
        />
        <YAxis
          type="number"
          dataKey="wr"
          domain={[0, 1]}
          name="win-rate"
          tickFormatter={(v) => `${Math.round(v * 100)}%`}
          tick={{ fontSize: 11, fill: C.axis }}
          axisLine={false}
          tickLine={false}
          width={40}
        />
        <ZAxis type="number" dataKey="n" range={[40, 320]} name="n" />
        <Tooltip content={<HeadroomTooltip />} cursor={{ strokeDasharray: "3 3" }} />
        <ReferenceLine y={HEADROOM_WR} stroke={C.grid} strokeDasharray="4 3" />
        <ReferenceLine x={HEADROOM_BID} stroke={C.grid} strokeDasharray="4 3"
          label={{ value: "100%", position: "top", fontSize: 9, fill: C.axis }} />
        <Scatter data={blue} fill={C.series} fillOpacity={0.55} />
        <Scatter data={green} fill={C.won} fillOpacity={0.85} />
      </ScatterChart>
    </ResponsiveContainer>
  );
}

export function CountBars({ data }: { data: CountRow[] }) {
  if (!data.length) return <Empty />;
  return (
    <ResponsiveContainer width="100%" height={220}>
      <BarChart data={data} margin={{ top: 8, right: 12, left: 4, bottom: 4 }}>
        <CartesianGrid vertical={false} stroke={C.grid} />
        <XAxis dataKey="category" tick={{ fontSize: 11, fill: C.axis }} axisLine={false} tickLine={false} />
        <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: C.axis }} axisLine={false} tickLine={false} width={40} />
        <Tooltip cursor={{ fill: "rgba(130,130,130,0.08)" }} />
        <Bar dataKey="n" fill={C.cancelled} maxBarSize={48} radius={[3, 3, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}
